import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import { GoogleLogin } from "@react-oauth/google";

import Button from "../../components/Button/Button3";
import LoadingOverlay from "../../components/LoadingOverlay/LoadingOverlay";
import Seo from "../../components/Seo/Seo";
import homeBackground from "../../assets/images/home_background.avif";
import { ROUTES } from "../../utils/appRoutes";
import {
  buildAuthPathWithNext,
  getPostAuthFallbackPath,
  getSafeNextFromLocation,
} from "../../utils/authRedirect";
import { getEmbeddedBrowserName } from "../../utils/embeddedBrowser";
import "./Login.css";                 // keep your normal styles

function Login({ onLogin }) {
  /* ───────────── local state ───────────── */
  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [captcha,      setCaptcha]      = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [msg,          setMsg]          = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = getSafeNextFromLocation(location, getPostAuthFallbackPath(location));
  const registerPath = buildAuthPathWithNext(ROUTES.REGISTER, nextPath);
  const forgotPasswordPath = buildAuthPathWithNext(ROUTES.FORGOT_PASSWORD, nextPath);
  const confirmPath = buildAuthPathWithNext(ROUTES.CONFIRM, nextPath);
  const embeddedBrowserName = getEmbeddedBrowserName();
  const showEmbeddedBrowserNotice = Boolean(embeddedBrowserName);

  useEffect(() => {
    if (!location.state?.email) return;
    setEmail(location.state.email);
  }, [location.state]);

  /* ───────────── helpers ───────────── */
  const saveTokens = ({ idToken, accessToken, refreshToken }) => {
    localStorage.setItem("idToken",      idToken);
    localStorage.setItem("accessToken",  accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    onLogin(idToken, accessToken, refreshToken); // let App know we’re authenticated
  };

  /* ───────────── email / password flow ───────────── */
  const handleLogin = async () => {
    if (loading) return;                    // prevent double-clicks
    if (!email || !password)  return setMsg("Fill in both fields.");
    if (!captcha)             return setMsg("Complete the captcha.");

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/login`,
        { email, password, captcha },
      );

      saveTokens(data);
      localStorage.setItem("userId",    data.userId);
      localStorage.setItem("firstName", data.firstName || "");
      navigate(nextPath, { replace: true });
    } catch (err) {
      const apiErr = err.response?.data?.error || "Login failed.";
      if (apiErr === "Incorrect username or password.")
        setMsg("Incorrect email or password.");
      else if (apiErr === "User not confirmed.") {
        setMsg("Account not confirmed - check your inbox and spam folder.");
        navigate(confirmPath, { state: { email, password, next: nextPath } });
      } else setMsg(apiErr);
    } finally {
      setLoading(false);
    }
  };

  /* ───────────── Google OAuth flow ───────────── */
  const handleGoogle = async ({ credential }) => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/google`,
        { credential },
      );

      saveTokens({
        idToken:      data.cognito.idToken,
        accessToken:  data.cognito.accessToken,
        refreshToken: data.cognito.refreshToken,
      });
      localStorage.setItem("userId",    data.user._id);
      localStorage.setItem("firstName", data.user.firstName || "");
      navigate(nextPath, { replace: true });
    } catch (err) {
      setMsg(err.response?.data?.error || "Google sign-in failed.");
    } finally {
      setLoading(false);
    }
  };

  /* ───────────── UI ───────────── */
  return (
    <>
      <Seo
        title="Login | ZealFC"
        description="Sign in to your ZealFC account to manage football games, registrations, and attendance."
        robots="noindex, nofollow"
        canonicalPath={ROUTES.LOGIN}
      />
      <div className="login-layout">
        <div className="login-image-container">
          <img src={homeBackground} alt="Background" className="login-image" />
          <div className="noise-effect" />
        </div>

        <div className="login-form-container">
          <p className="login-title">WELCOME&nbsp;BACK!</p>

          <form className="login-form" onSubmit={e => e.preventDefault()}>
            {/* email / password */}
            <div className="login-field">
              <span>Email:</span>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="login-input-line"
              />
            </div>

            <div className="login-field">
              <span>Password:</span>
              <div className="password-container">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="login-input-line"
                />
                <span
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Hide" : "SHOW"}
                </span>
              </div>
            </div>
            <Button
              variant="small"
              primaryText={loading ? "Logging in…" : "Log in"}
              onClick={handleLogin}
            />

            {/*  Google Sign-in  */}
            {showEmbeddedBrowserNotice ? (
              <div className="auth-browser-notice" role="status">
                <span className="auth-browser-notice-label">In-App Browser</span>
                <p className="auth-browser-notice-copy">
                  Google sign-in is not supported inside {embeddedBrowserName}. Open this page in
                  Chrome or Safari to continue with Google, or use your email and password here.
                </p>
              </div>
            ) : (
              <div className="google-wrapper">
                <GoogleLogin
                  theme="filled_black"
                  text="signin_with"
                  width="300"
                  onSuccess={handleGoogle}
                  onError={() => setMsg("Google sign-in cancelled or failed.")}
                />
              </div>
            )}

            {/* reCAPTCHA (email login only) */}
            <div className="captcha-field">
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={setCaptcha}
                theme="dark"
              />
            </div>
          </form>

          {msg && <p className="login-message">{msg}</p>}

          <div className="login-links">
            <div className="login-link">
              <span>Don't have an account?</span>
              <Link to={registerPath} className="login-link-text"> Register</Link>
            </div>
            <div className="login-link">
              <span>Forgot your password?</span>
              <Link to={forgotPasswordPath} className="login-link-text"> Reset Password</Link>
            </div>
          </div>
        </div>
      </div>

      {/* loading spinner */}
      {loading && <LoadingOverlay label="Logging in" />}
    </>
  );
}

export default Login;
